import React, { useEffect, useState } from "react";
import api from "../../services/api";
import {
  PageContainer, PageHeader, Card, CardHeader,
  Table, Thead, Th, Td, TRow,
  Button, FormGroup, Input, Select,
  EmptyState, Muted,
} from "../ui";

interface Tap {
  id: string;
  tap_number: number;
  product_id: string;
  product_name: string;
  brand: string;
  category: string;
  keg_size: string;
  oz_capacity: number;
  oz_remaining: number;
  percent_remaining: number;
  days_on_tap: number;
  oz_per_day: number;
  projected_days_remaining: number | null;
  tapped_at: string;
  notes: string | null;
  is_active: boolean;
}

interface KegSize {
  name: string;
  label: string;
  oz: number;
  gallons: number;
}

interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
}

interface HistoryEntry {
  id: string;
  product_name: string;
  brand: string;
  keg_size: string;
  oz_capacity: number;
  oz_poured: number;
  days_lasted: number;
  tapped_at: string;
  kicked_at: string;
}

export default function KegDashboard() {
  const [taps, setTaps] = useState<Tap[]>([]);
  const [sizes, setSizes] = useState<KegSize[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showTapForm, setShowTapForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [tapForm, setTapForm] = useState({ tap_number: "", product_id: "", keg_size: "sixth_barrel", notes: "" });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [tapsRes, sizesRes] = await Promise.all([
      api.get("/kegs/taps"),
      api.get("/kegs/sizes"),
    ]);
    setTaps(tapsRes.data);
    setSizes(sizesRes.data);
  };

  const loadHistory = async () => {
    const res = await api.get("/kegs/history");
    setHistory(res.data);
    setShowHistory(true);
  };

  const loadProducts = async () => {
    const res = await api.get("/products?is_active=true");
    setProducts(res.data);
  };

  const openTapForm = () => {
    loadProducts();
    setShowTapForm(true);
  };

  const submitTap = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post("/kegs/tap", {
      tap_number: Number(tapForm.tap_number),
      product_id: tapForm.product_id,
      keg_size: tapForm.keg_size,
      notes: tapForm.notes || undefined,
    });
    setShowTapForm(false);
    setTapForm({ tap_number: "", product_id: "", keg_size: "sixth_barrel", notes: "" });
    loadData();
  };

  const kickKeg = async (tapId: string) => {
    if (!window.confirm("Mark this keg as kicked?")) return;
    await api.post(`/kegs/${tapId}/kick`);
    loadData();
  };

  const getBarColor = (percent: number): string => {
    if (percent > 50) return "var(--rbw-success)";
    if (percent > 25) return "var(--rbw-gold)";
    if (percent > 10) return "#D97706";
    return "var(--rbw-danger)";
  };

  const formatKegSize = (size: string): string => {
    return size.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <PageContainer>
      <PageHeader
        title="Keg Tap Status"
        subtitle={`${taps.length} active tap${taps.length !== 1 ? "s" : ""}`}
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => showHistory ? setShowHistory(false) : loadHistory()}>
              {showHistory ? "Current Taps" : "History"}
            </Button>
            <Button onClick={openTapForm}>Tap New Keg</Button>
          </div>
        }
      />

      {showTapForm && (
        <Card className="mb-6">
          <CardHeader title="Tap a New Keg" />
          <form onSubmit={submitTap} className="grid grid-cols-2 gap-4">
            <FormGroup label="Tap Number">
              <Input
                type="number"
                value={tapForm.tap_number}
                onChange={(v) => setTapForm({ ...tapForm, tap_number: v })}
                placeholder="1"
                min="1"
                required
              />
            </FormGroup>
            <FormGroup label="Keg Size">
              <Select value={tapForm.keg_size} onChange={(v) => setTapForm({ ...tapForm, keg_size: v })}>
                {sizes.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.label} ({s.gallons} gal / {s.oz} oz)
                  </option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Product" span={2}>
              <Select value={tapForm.product_id} onChange={(v) => setTapForm({ ...tapForm, product_id: v })}>
                <option value="">Select a product...</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Notes (optional)" span={2}>
              <Input
                value={tapForm.notes}
                onChange={(v) => setTapForm({ ...tapForm, notes: v })}
                placeholder="Batch number, special notes..."
              />
            </FormGroup>
            <div className="col-span-2 flex gap-2">
              <Button type="submit">Tap Keg</Button>
              <Button variant="ghost" onClick={() => setShowTapForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {showHistory && (
        <Table>
          <Thead>
            <Th>Beer</Th>
            <Th>Size</Th>
            <Th align="right">Oz Poured</Th>
            <Th align="right">Days</Th>
            <Th>Tapped</Th>
            <Th>Kicked</Th>
          </Thead>
          <tbody>
            {history.map((h, idx) => (
              <TRow key={h.id} index={idx}>
                <Td>
                  <div className="font-medium" style={{ color: "var(--rbw-navy)" }}>{h.product_name}</div>
                  <Muted>{h.brand}</Muted>
                </Td>
                <Td>{formatKegSize(h.keg_size)}</Td>
                <Td align="right">{Number(h.oz_poured).toFixed(0)} oz</Td>
                <Td align="right">{Number(h.days_lasted).toFixed(0)}</Td>
                <Td>{new Date(h.tapped_at).toLocaleDateString()}</Td>
                <Td>{new Date(h.kicked_at).toLocaleDateString()}</Td>
              </TRow>
            ))}
            {history.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <EmptyState message="No keg history yet" />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {!showHistory && (
        <>
          {taps.length === 0 ? (
            <Card>
              <EmptyState message="No kegs on tap" detail="Tap a new keg to start tracking" icon="&#127866;" />
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {taps.map((tap) => {
                const percent = Number(tap.percent_remaining);
                const ozRemaining = Number(tap.oz_remaining);
                const ozCapacity = Number(tap.oz_capacity);
                const daysOnTap = Number(tap.days_on_tap);
                const gallonsRemaining = (ozRemaining / 128).toFixed(1);
                const pintsRemaining = Math.floor(ozRemaining / 16);

                return (
                  <Card key={tap.id} padding="p-0" className="overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3 flex justify-between items-center" style={{ background: "var(--rbw-navy)" }}>
                      <div className="font-bold text-lg" style={{ color: "#fff" }}>Tap {tap.tap_number}</div>
                      <span className="text-xs px-2 py-1 rounded" style={{ background: "rgba(0,0,0,0.2)", color: "var(--rbw-gray)" }}>
                        {formatKegSize(tap.keg_size)}
                      </span>
                    </div>

                    {/* Beer info */}
                    <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--rbw-border)" }}>
                      <div className="font-bold text-lg" style={{ color: "var(--rbw-navy)" }}>
                        {tap.brand || tap.product_name}
                      </div>
                      <Muted>{tap.product_name}</Muted>
                    </div>

                    {/* Level bar */}
                    <div className="px-4 py-3">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium" style={{ color: "var(--rbw-navy)" }}>{percent}% remaining</span>
                        <Muted>{gallonsRemaining} gal</Muted>
                      </div>
                      <div className="w-full rounded-full h-4 overflow-hidden" style={{ background: "var(--rbw-gray-light)" }}>
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${Math.max(2, percent)}%`, background: getBarColor(percent) }}
                        />
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <Muted>{ozRemaining.toFixed(0)} / {ozCapacity.toFixed(0)} oz</Muted>
                        <Muted>~{pintsRemaining} pints left</Muted>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="px-4 py-3 grid grid-cols-3 gap-2 text-center text-sm" style={{ background: "var(--rbw-bg)" }}>
                      <div>
                        <div className="text-xs"><Muted>On Tap</Muted></div>
                        <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>{daysOnTap}d</div>
                      </div>
                      <div>
                        <div className="text-xs"><Muted>Avg/Day</Muted></div>
                        <div className="font-bold" style={{ color: "var(--rbw-navy)" }}>{tap.oz_per_day || 0} oz</div>
                      </div>
                      <div>
                        <div className="text-xs"><Muted>Days Left</Muted></div>
                        <div className="font-bold" style={{
                          color: tap.projected_days_remaining !== null && tap.projected_days_remaining < 3
                            ? "var(--rbw-danger)" : "var(--rbw-navy)"
                        }}>
                          {tap.projected_days_remaining !== null ? `~${tap.projected_days_remaining}` : "--"}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="px-4 py-2" style={{ borderTop: "1px solid var(--rbw-border)" }}>
                      <Button variant="danger" size="sm" onClick={() => kickKeg(tap.id)} className="w-full">
                        Kick Keg
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
